import type {
  NotificationSetting,
  NotificationSettingRequest,
} from 'src/api-sdk';
import type { NotificationType } from 'src/util/notificationConfig';
import { safeGetItem, safeSetItem } from 'src/util/safe-storage';

type NotificationSettingsApi = {
  activitiesNotificationSettingsList: () => Promise<{
    data: NotificationSetting[];
  }>;
  activitiesNotificationSettingsUpdate: (requestParameters: {
    notificationSettingRequest: NotificationSettingRequest[];
  }) => Promise<{ data: NotificationSetting[] }>;
};

// Email delivery is not supported yet and the API only accepts a boolean
// in_app_enabled, so each setting is a simple on/off toggle.
export type NotificationChannel = 'off' | 'on';

export type SnoozeDuration = '30m' | '1h' | '4h' | 'tomorrow' | 'indefinitely';

export const SNOOZE_INDEFINITELY = Number.MAX_SAFE_INTEGER;

export type ChannelSettingKey =
  | 'complianceComment'
  | 'complianceCommentMention'
  | 'binderComment'
  | 'binderCommentMention'
  | 'companyCompliance'
  | 'companyRegistration'
  | 'companyFeedback'
  | 'companyInvestigation'
  | 'batchRegistration'
  | 'batchComplianceCheck'
  | 'articles'
  | 'exports'
  | 'supportRequests'
  | 'accountChanges';

export type NotificationSettings = {
  inApp: boolean;
  showBadge: boolean;
  snoozedUntil: number | null;
  complianceComment: NotificationChannel;
  complianceCommentMention: NotificationChannel;
  binderComment: NotificationChannel;
  binderCommentMention: NotificationChannel;
  companyCompliance: NotificationChannel;
  companyRegistration: NotificationChannel;
  companyFeedback: NotificationChannel;
  companyInvestigation: NotificationChannel;
  batchRegistration: NotificationChannel;
  batchComplianceCheck: NotificationChannel;
  articles: NotificationChannel;
  exports: NotificationChannel;
  supportRequests: NotificationChannel;
  accountChanges: NotificationChannel;
};

export const NOTIFICATION_SETTINGS_STORAGE_KEY =
  'account-notification-settings';

export const NOTIFICATION_SETTINGS_CHANGED_AT_KEY =
  'account-notification-settings-changed-at';

export const NOTIFICATION_CHANNEL_VALUES: NotificationChannel[] = ['off', 'on'];

export const defaultNotificationSettings: NotificationSettings = {
  inApp: true,
  showBadge: true,
  snoozedUntil: null,
  complianceComment: 'on',
  complianceCommentMention: 'on',
  binderComment: 'on',
  binderCommentMention: 'on',
  companyCompliance: 'on',
  companyRegistration: 'on',
  companyFeedback: 'on',
  companyInvestigation: 'on',
  batchRegistration: 'on',
  batchComplianceCheck: 'on',
  articles: 'on',
  exports: 'on',
  supportRequests: 'on',
  accountChanges: 'on',
};

export const settingsKeyI18nTitle: Record<ChannelSettingKey, string> = {
  complianceComment: 'Compliance comments',
  complianceCommentMention: 'Compliance comment mentions',
  binderComment: 'Binder comments',
  binderCommentMention: 'Binder comment mentions',
  companyCompliance: 'Compliance check updates',
  companyRegistration: 'Company registration updates',
  companyFeedback: 'Company feedback',
  companyInvestigation: 'Missing company investigations',
  batchRegistration: 'Batch registrations',
  batchComplianceCheck: 'Batch compliance checks',
  articles: 'Article feedback',
  exports: 'Exports',
  supportRequests: 'Support request updates',
  accountChanges: 'Account changes',
};

export const getSettingsKey = (
  notificationType: NotificationType
): ChannelSettingKey | undefined => {
  const exactMatch = Object.entries(notificationTypesBySettingsKey).find(
    ([, notificationTypes]) => notificationTypes.includes(notificationType)
  );

  if (exactMatch) return exactMatch[0] as ChannelSettingKey;
  return undefined;
};

export const notificationTypesBySettingsKey: Record<
  ChannelSettingKey,
  NotificationType[]
> = {
  complianceComment: ['COMMENT'],
  complianceCommentMention: ['COMMENT_MENTION'],
  binderComment: ['BINDER_COMMENT'],
  binderCommentMention: ['BINDER_COMMENT_MENTION'],
  companyCompliance: ['COMPLIANCE_CHECK_UPDATE'],
  companyRegistration: ['COMPANY_REGISTRATION_UPDATE'],
  companyFeedback: ['COMPANY_FEEDBACK_RECEIVED'],
  companyInvestigation: ['MISSING_COMPANY_INVESTIGATION_UPDATE'],
  batchRegistration: ['BATCH_REGISTRATION_UPDATE'],
  batchComplianceCheck: ['BATCH_COMPLIANCE_CHECK_UPDATE'],
  articles: ['NEWS_ARTICLE_FEEDBACK_RECEIVED'],
  exports: ['EXPORT_UPDATE'],
  supportRequests: ['SUPPORT_REQUEST_UPDATE'],
  accountChanges: ['USER_DETAILS_UPDATE'],
};

// Keys whose notification types are not accepted by the settings API endpoint.
const settingsKeysExcludedFromApi: ChannelSettingKey[] = ['accountChanges'];

const notificationTypesBackedByApi = Object.entries(
  notificationTypesBySettingsKey
)
  .filter(
    ([key]) => !settingsKeysExcludedFromApi.includes(key as ChannelSettingKey)
  )
  .flatMap(([, notificationTypes]) => notificationTypes);

export const isInAppChannel = (channel: NotificationChannel): boolean =>
  channel === 'on';

const toSyncedChannel = (
  enabled: boolean,
  currentChannel: NotificationChannel,
  preserveWhenDisabled: boolean
): NotificationChannel => {
  if (enabled) {
    return isInAppChannel(currentChannel) ? currentChannel : 'on';
  }

  return preserveWhenDisabled ? currentChannel : 'off';
};

export const apiNotificationSettingsToSettings = (
  apiSettings: NotificationSetting[],
  currentSettings: NotificationSettings = readNotificationSettings()
): NotificationSettings => {
  const next: NotificationSettings = {
    ...defaultNotificationSettings,
    ...currentSettings,
    showBadge: currentSettings.showBadge,
    snoozedUntil: currentSettings.snoozedUntil,
  };

  const enabledByType = new Map(
    apiSettings.map((setting) => [
      setting.notification_type as NotificationType,
      setting.in_app_enabled,
    ])
  );

  Object.entries(notificationTypesBySettingsKey).forEach(
    ([key, notificationTypes]) => {
      const settingsKey = key as ChannelSettingKey;
      const knownValues = notificationTypes
        .map((notificationType) => enabledByType.get(notificationType))
        .filter((value): value is boolean => typeof value === 'boolean');

      if (knownValues.length > 0) {
        next[settingsKey] = toSyncedChannel(
          knownValues.some(Boolean),
          currentSettings[settingsKey],
          !currentSettings.inApp
        );
      }
    }
  );

  const knownInAppValues = notificationTypesBackedByApi
    .map((notificationType) => enabledByType.get(notificationType))
    .filter((value): value is boolean => typeof value === 'boolean');

  next.inApp =
    knownInAppValues.length === notificationTypesBackedByApi.length
      ? knownInAppValues.some(Boolean)
      : currentSettings.inApp;

  return next;
};

export const settingsToApiNotificationSettings = (
  settings: NotificationSettings
): NotificationSettingRequest[] =>
  Object.entries(notificationTypesBySettingsKey).flatMap(
    ([key, notificationTypes]) => {
      if (settingsKeysExcludedFromApi.includes(key as ChannelSettingKey)) {
        return [];
      }
      return notificationTypes.map((notificationType) => ({
        notification_type:
          notificationType as NotificationSettingRequest['notification_type'],
        in_app_enabled:
          settings.inApp && isInAppChannel(settings[key as ChannelSettingKey]),
      }));
    }
  );

// Returns the API payload for a single settings key (one entry per notification type).
export const settingsKeyToApiNotificationSettings = (
  key: ChannelSettingKey,
  settings: NotificationSettings
): NotificationSettingRequest[] => {
  if (settingsKeysExcludedFromApi.includes(key)) return [];
  return (notificationTypesBySettingsKey[key] ?? []).map(
    (notificationType) => ({
      notification_type:
        notificationType as NotificationSettingRequest['notification_type'],
      in_app_enabled: settings.inApp && isInAppChannel(settings[key]),
    })
  );
};

export const fetchApiNotificationSettings = async (
  activitiesAPI: NotificationSettingsApi | null | undefined
): Promise<NotificationSetting[]> => {
  if (activitiesAPI) {
    const response = await activitiesAPI.activitiesNotificationSettingsList();
    return response.data;
  }

  return [];
};

export const updateApiNotificationSettings = async (
  activitiesAPI: NotificationSettingsApi | null | undefined,
  notificationSettingRequest: NotificationSettingRequest[]
): Promise<NotificationSetting[]> => {
  if (activitiesAPI) {
    const response = await activitiesAPI.activitiesNotificationSettingsUpdate({
      notificationSettingRequest,
    });
    return response.data;
  }

  return [];
};

export const writeApiNotificationSettings = (
  apiSettings: NotificationSetting[]
): NotificationSettings => {
  const settings = apiNotificationSettingsToSettings(apiSettings);
  writeNotificationSettings(settings);
  return settings;
};

export const readNotificationSettings = (): NotificationSettings => {
  try {
    const raw = safeGetItem('localStorage', NOTIFICATION_SETTINGS_STORAGE_KEY);
    if (!raw) return defaultNotificationSettings;
    const parsed = JSON.parse(raw) as Partial<NotificationSettings>;
    return { ...defaultNotificationSettings, ...parsed };
  } catch {
    return defaultNotificationSettings;
  }
};

export const getSnoozedUntilMs = (duration: SnoozeDuration): number => {
  const now = Date.now();
  switch (duration) {
    case '30m':
      return now + 30 * 60 * 1000;
    case '1h':
      return now + 60 * 60 * 1000;
    case '4h':
      return now + 4 * 60 * 60 * 1000;
    case 'tomorrow': {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(8, 0, 0, 0);
      return tomorrow.getTime();
    }
    case 'indefinitely':
      return SNOOZE_INDEFINITELY;
  }
};

export const isSnoozed = (): boolean => {
  const settings = readNotificationSettings();
  if (!settings.snoozedUntil) return false;
  if (Date.now() < settings.snoozedUntil) return true;
  // Snooze expired — clear it from storage.
  writeNotificationSettings({ ...settings, snoozedUntil: null });
  return false;
};

export const isBadgeEnabled = (): boolean => {
  const settings = readNotificationSettings();
  return settings.showBadge && !isSnoozed();
};

export const isNotificationTypeEnabled = (
  notificationType: NotificationType
): boolean => {
  if (isSnoozed()) return false;
  const settings = readNotificationSettings();
  if (!settings.inApp) return false;
  const key = getSettingsKey(notificationType);
  if (!key) return true;
  return isInAppChannel(settings[key]);
};

const writeNotificationSettingsChangedAt = (): void => {
  safeSetItem(
    'localStorage',
    NOTIFICATION_SETTINGS_CHANGED_AT_KEY,
    String(Date.now())
  );
};

export const writeNotificationSettings = (
  settings: NotificationSettings
): void => {
  safeSetItem(
    'localStorage',
    NOTIFICATION_SETTINGS_STORAGE_KEY,
    JSON.stringify(settings)
  );
  writeNotificationSettingsChangedAt();
};
